import { ReactNode, useCallback, useState } from 'react';
import SharedDialog from '../../../../shared/shared-dialog';
import ConnectingDialog from './connecting-dialog';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import NotInterested from '@mui/icons-material/NotInterested';
import type { SxProps, Theme } from '@mui/material/styles';

export interface IntegrationCardProps {
  logo: ReactNode | string;
  title: string;
  description?: ReactNode;
  content?: ReactNode;
  children?: ReactNode;
  contentHeight?: number | string;
  isLoading?: boolean;
  isConnected?: boolean;
  connectedLabel?: string;
  notConnectedLabel?: string;
  primaryButtonLabel?: ReactNode;
  onPrimaryButtonClick?: () => void;
  primaryButtonDisabled?: boolean;
  primaryButtonLoading?: boolean;
  primaryButtonTooltip?: string;
  isConnectingDialogOpen?: boolean;
  onConnectingDialogClose?: () => void;
  connectingProgress?: number;
  isDisconnectDialogOpen?: boolean;
  onDisconnectDialogClose?: () => void;
  onDisconnectConfirm?: () => void;
  skipDisconnectConfirmDialog?: boolean;
  secondaryButtonLabel?: ReactNode;
  onSecondaryButtonClick?: () => void;
  secondaryButtonDisabled?: boolean;
  sx?: SxProps<Theme>;
}

export default function IntegrationCard({
  logo,
  title,
  description,
  content,
  children,
  contentHeight = 180,
  isLoading = false,
  isConnected = false,
  connectedLabel = 'Connected',
  notConnectedLabel = 'Not Connected',
  primaryButtonLabel,
  onPrimaryButtonClick,
  primaryButtonDisabled = false,
  primaryButtonLoading = false,
  primaryButtonTooltip = '',
  isConnectingDialogOpen = false,
  onConnectingDialogClose,
  connectingProgress,
  isDisconnectDialogOpen,
  onDisconnectDialogClose,
  onDisconnectConfirm,
  skipDisconnectConfirmDialog = false,
  secondaryButtonLabel,
  onSecondaryButtonClick,
  secondaryButtonDisabled = false,
  sx,
}: IntegrationCardProps) {
  const [internalDisconnectDialogOpen, setInternalDisconnectDialogOpen] =
    useState(false);

  const isDisconnectOpen =
    isDisconnectDialogOpen !== undefined
      ? isDisconnectDialogOpen
      : internalDisconnectDialogOpen;

  const handlePrimaryButtonClick = () => {
    if (isConnected) {
      if (skipDisconnectConfirmDialog) {
        onDisconnectConfirm?.();
        return;
      }
      setInternalDisconnectDialogOpen(true);
      return;
    }
    onPrimaryButtonClick?.();
  };

  const handleCloseDisconnectDialog = () => {
    setInternalDisconnectDialogOpen(false);
    onDisconnectDialogClose?.();
  };

  const handleConfirmDisconnect = () => {
    setInternalDisconnectDialogOpen(false);
    onDisconnectDialogClose?.();
    onDisconnectConfirm?.();
  };

  const handleCloseConnectingDialog = useCallback(() => {
    onConnectingDialogClose?.();
  }, [onConnectingDialogClose]);

  const resolvedPrimaryButtonLabel = isConnected
    ? `Disconnect from ${title}`
    : primaryButtonLabel ?? `Connect to ${title}`;

  const cardBody = content ?? children;

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        sx={{
          width: 592,
          height: 360,
          borderRadius: '8px',
          ...sx,
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: 592,
        height: 360,
        display: 'flex',
        padding: '24px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        borderRadius: '8px',
        border: (theme) =>
          `var(--half, 0.5px) solid var(--Background-Outline-Light, ${theme.palette.semantic.outline.light})`,
        '& .MuiAlert-root, & .MuiAlert-message, & .MuiPaper-outlined > .MuiBox-root > .MuiTypography-root':
          {
            fontSize: '13px',
          },
        ...sx,
      }}
    >
      <Stack spacing={1} sx={{ width: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                border: (theme) =>
                  `var(--half, 0.5px) solid var(--Background-Outline-Light, ${theme.palette.semantic.outline.light})`,
                flexShrink: 0,
              }}
            >
              {typeof logo === 'string' ? (
                <Box
                  component="img"
                  src={logo}
                  alt={title}
                  sx={{ width: 48, height: 48 }}
                />
              ) : (
                logo
              )}
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                fontWeight: 500,
                color: (theme) => theme.palette.semantic.text.title2,
              }}
            >
              {title}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.75}>
            {isConnected ? (
              <CheckCircleOutlined
                sx={{
                  fontSize: 18,
                  color: (theme) => theme.palette.semantic.icon.success,
                }}
              />
            ) : (
              <NotInterested
                sx={{
                  fontSize: 18,
                  color: (theme) => theme.palette.text.disabled,
                }}
              />
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: (theme) =>
                  isConnected
                    ? theme.palette.semantic.icon.success
                    : theme.palette.text.disabled,
              }}
            >
              {isConnected ? connectedLabel : notConnectedLabel}
            </Typography>
          </Stack>
        </Stack>

        {description && <Typography variant="body2">{description}</Typography>}
      </Stack>

      {cardBody && (
        <Box
          sx={{
            width: '100%',
            height: contentHeight,
            overflowY: 'auto',
          }}
        >
          {cardBody}
        </Box>
      )}

      {(secondaryButtonLabel || resolvedPrimaryButtonLabel) && (
        <Stack direction="row" spacing={2} sx={{ width: '100%', mt: 'auto' }}>
          {secondaryButtonLabel && (
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
              <Button
                variant="outlined"
                color="inherit"
                fullWidth
                disabled={secondaryButtonDisabled}
                onClick={onSecondaryButtonClick}
                sx={{
                  textTransform: 'none',
                  borderColor: (theme) =>
                    theme.palette.semantic.button.secondary.border,
                  color: (theme) =>
                    theme.palette.semantic.button.secondary.text,
                  '&:hover': {
                    borderColor: (theme) =>
                      theme.palette.semantic.button.secondary.border,
                  },
                }}
              >
                {secondaryButtonLabel}
              </Button>
            </Box>
          )}
          {resolvedPrimaryButtonLabel && (
            <Tooltip arrow title={primaryButtonTooltip}>
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={primaryButtonDisabled}
                  loading={primaryButtonLoading}
                  onClick={handlePrimaryButtonClick}
                  sx={{ textTransform: 'none' }}
                >
                  {resolvedPrimaryButtonLabel}
                </Button>
              </Box>
            </Tooltip>
          )}
        </Stack>
      )}

      <ConnectingDialog
        open={isConnectingDialogOpen}
        onClose={handleCloseConnectingDialog}
        integrationLogo={logo}
        integrationName={title}
        progress={connectingProgress}
      />
      <SharedDialog
        open={isDisconnectOpen}
        handleClose={handleCloseDisconnectDialog}
        title={`Disconnect from ${title}`}
        maxWidth="xs"
        dialogContent={
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to disconnect from {title}?
          </Typography>
        }
        primaryButton={{
          title: 'Disconnect',
          color: 'error',
          action: handleConfirmDisconnect,
        }}
      />
    </Box>
  );
}
